import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Pencil, Trash, Search, Ban, CheckCircle, ExternalLink, Tag, Calendar, CreditCard } from "lucide-react";
import { formatDate } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface Vendor {
  id: string;
  name: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  is_active: boolean;
  family_id: string;
  sku?: string;
  vendor_id?: string;
  vendor_subscription_name?: string;
  vendor_part_number?: string;
  pricing_model?: string;
  unit_measure?: string;
  minimum_quantity?: number;
  maximum_quantity?: number;
  billing_model?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  subscription_period_type?: string;
  subscription_period_duration?: number;
  vendor?: Vendor;
  prices?: SubscriptionPlanPrice[];
}

interface SubscriptionPlanTableProps {
  familyId?: string;
  refreshTrigger?: number;
  onImport?: () => void;
  searchTerm?: string;
  showInactive?: boolean;
  viewMode?: 'grid' | 'list';
}

export default function SubscriptionPlanTable({ 
  familyId, 
  refreshTrigger = 0, 
  onImport,
  searchTerm = '',
  showInactive = false,
  viewMode = 'list'
}: SubscriptionPlanTableProps) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<SubscriptionPlan[]>([]);
  const [nameFilter, setNameFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = viewMode === 'grid' ? 12 : 10;
  const { toast } = useToast();

  const fetchPlans = async () => {
    try {
      let query = supabase
        .from('subscription_plans')
        .select(`
          *,
          prices:subscription_plan_prices (*)
        `)
        .order('name', { ascending: true });
      
      // Only filter by family_id if a specific family is selected (not "all")
      if (familyId && familyId !== 'all') {
        query = query.eq('family_id', familyId);
      }
      
      // Only filter by active status if showInactive is false
      if (!showInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPlans(data || []);
      applyFilters(data || [], searchTerm || nameFilter);
    } catch (error) {
      console.error('Error fetching plans:', error, { familyId });
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar planos',
        description: 'Não foi possível carregar os planos. Por favor, recarregue a página.',
      });
    }
  };

  const applyFilters = (data: SubscriptionPlan[], name: string) => {
    let result = [...data];
    
    if (name) {
      result = result.filter(plan => 
        plan.name.toLowerCase().includes(name.toLowerCase()) ||
        plan.sku?.toLowerCase().includes(name.toLowerCase()) ||
        plan.description?.toLowerCase().includes(name.toLowerCase())
      );
    }
    
    setTotalPages(Math.max(1, Math.ceil(result.length / itemsPerPage)));
    setFilteredPlans(result);
    
    if (currentPage > Math.ceil(result.length / itemsPerPage)) {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [familyId, refreshTrigger, showInactive]);

  useEffect(() => {
    applyFilters(plans, searchTerm || nameFilter);
  }, [searchTerm, nameFilter, currentPage, itemsPerPage]);

  // Reset page when changing view mode
  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode]);

  const handleEdit = (plan: SubscriptionPlan) => {
    navigate(`/subscription-plans/edit/${plan.id}`);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Plano inativado',
        description: 'O plano foi inativado com sucesso.',
      });

      fetchPlans();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao inativar plano',
        description: 'Não foi possível inativar o plano. Por favor, tente novamente.',
      });
    }
  };

  const handleActivate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: true })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Plano ativado',
        description: 'O plano foi ativado com sucesso.',
      });

      fetchPlans();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao ativar plano',
        description: 'Não foi possível ativar o plano. Por favor, tente novamente.',
      });
    }
  };

  const handleViewDetails = (plan: SubscriptionPlan) => {
    navigate(`/subscription-plans/details/${plan.id}`);
  };

  const formatPrice = (plan: SubscriptionPlan) => {
    let priceFormatted = '';
    let currency = 'BRL';
    
    // If the plan has prices, try to use BRL first, then USD if BRL not available
    if (plan.prices && plan.prices.length > 0) {
      // Try to find a BRL price first
      const brlPrice = plan.prices.find(price => price.currency === 'BRL');
      
      // If no BRL price, try to find a USD price
      const usdPrice = plan.prices.find(price => price.currency === 'USD');
      
      if (brlPrice) {
        // Use BRL price if available
        priceFormatted = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(brlPrice.price_amount);
        currency = 'BRL';
      } else if (usdPrice) {
        // Use USD price if BRL not available
        priceFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(usdPrice.price_amount);
        currency = 'USD';
      } else {
        // Fallback to first price in any currency
        const firstPrice = plan.prices[0];
        priceFormatted = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: firstPrice.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(firstPrice.price_amount);
        currency = firstPrice.currency;
      }
    } else {
      // Fallback to the price field in the plan
      priceFormatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: plan.currency || 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(plan.price);
      currency = plan.currency || 'BRL';
    }
    
    return priceFormatted;
  };

  const getSubscriptionPeriod = (plan: SubscriptionPlan) => {
    if (!plan.subscription_period_type) return '-';
    
    switch (plan.subscription_period_type) {
      case 'month':
        return 'Mensal';
      case 'year':
        return 'Anual';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return plan.subscription_period_type.charAt(0).toUpperCase() + plan.subscription_period_type.slice(1);
    }
  };

  const getBillingPeriod = (plan: SubscriptionPlan) => {
    switch (plan.billing_period_type) {
      case 'month':
        return 'Mensal';
      case 'quarter':
        return 'Trimestral';
      case 'semiannual':
        return 'Semestral';
      case 'year':
        return 'Anual';
      case 'one-time':
        return 'Pagamento Único';
      default:
        return plan.billing_period_type || '-';
    }
  };

  const formatBillingModel = (plan: SubscriptionPlan) => {
    if (plan.billing_model === 'on creation') {
      return 'Na criação';
    } else if (plan.billing_model === 'on activation') {
      return 'Na ativação';
    } else if (plan.billing_model === 'on usage') {
      return 'No uso';
    } else if (plan.billing_model === 'subscription') {
      return 'Assinatura';
    } else if (plan.billing_model === 'one-time') {
      return 'Pagamento Único';
    }
    
    return plan.billing_model || '-';
  };

  const formatPricingModel = (plan: SubscriptionPlan) => {
    if (plan.pricing_model === 'flat') {
      return 'Valor Fixo';
    } else if (plan.pricing_model === 'per unit') {
      return `Por Unidade${plan.unit_measure ? ` (${plan.unit_measure})` : ''}`;
    }
    
    return plan.pricing_model || '-';
  };

  const formatVendor = (plan: SubscriptionPlan) => {
    if (plan.vendor) {
      return plan.vendor.name;
    } else if (plan.vendor_id) {
      return 'Fornecedor';
    }
    
    return '-';
  };

  const formatUnitMeasure = (unitMeasure?: string) => {
    if (!unitMeasure) return '';
    
    const unitMap: Record<string, string> = {
      'user': 'por usuário',
      'hour': 'por hora',
      'gb': 'por GB',
      'license': 'por licença',
      'seat': 'por assento',
      'instance': 'por instância',
      'api_call': 'por chamada de API',
      'transaction': 'por transação'
    };
    
    return unitMap[unitMeasure.toLowerCase()] || `por ${unitMeasure}`;
  };

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPlans.slice(startIndex, endIndex);
  };

  // Grid view action menu
  const PlanContextMenu = ({ plan }: { plan: SubscriptionPlan }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0 absolute top-2 right-2"
        >
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Ações</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleViewDetails(plan)}>
          <ExternalLink className="mr-2 h-4 w-4" />
          Detalhes
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleEdit(plan)}>
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </DropdownMenuItem>
        {plan.is_active ? (
          <DropdownMenuItem onClick={() => handleDelete(plan.id)}>
            <Ban className="mr-2 h-4 w-4" />
            Inativar
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => handleActivate(plan.id)}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Ativar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderEmptyState = () => (
    <div className="text-center p-6 border rounded-md">
      <div className="py-12">
        <Tag className="h-10 w-10 text-muted-foreground mx-auto mb-4 opacity-30" />
        <h3 className="text-lg font-extrabold mb-2">Nenhum plano encontrado</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          {searchTerm || nameFilter 
            ? 'Tente alterar sua busca para encontrar o que está procurando.' 
            : 'Crie um novo plano para esta família ou importe planos existentes.'}
        </p>
        <div className="flex gap-2 justify-center">
          {(searchTerm || nameFilter) && (
            <Button 
              variant="outline" 
              onClick={() => {
                setNameFilter('');
                // Reset external search if component is controlled
                if (onImport) onImport();
              }}
            >
              Limpar Filtros
            </Button>
          )}
          <Button onClick={() => navigate('/subscription-plans/new')}>
            Criar Novo Plano
          </Button>
        </div>
      </div>
    </div>
  );

  const renderGridView = () => {
    const paginatedData = getPaginatedData();
    
    if (paginatedData.length === 0) {
      return renderEmptyState();
    }
    
    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedData.map((plan) => (
            <Card 
              key={plan.id} 
              className={cn(
                "relative cursor-pointer hover:shadow-md transition-shadow", 
                !plan.is_active && "opacity-70"
              )}
              onClick={() => handleViewDetails(plan)}
            >
              <PlanContextMenu plan={plan} />
              
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-extrabold text-lg">{plan.name}</h3>
                    {plan.sku && (
                      <div className="flex items-center text-xs text-muted-foreground mt-1">
                        <Tag className="h-3 w-3 mr-1" />
                        {plan.sku}
                      </div>
                    )}
                  </div>
                  
                  {plan.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{plan.description}</p>
                  )}
                  
                  <div className="flex items-center gap-2 pt-1 text-sm">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {getBillingPeriod(plan)}
                    </Badge>
                    
                    <Badge 
                      className={cn(
                        plan.is_active
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      )}
                    >
                      {plan.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="px-6 py-4 border-t flex justify-between items-center bg-muted/50">
                <div className="font-extrabold text-lg">{formatPrice(plan)}</div>
                {plan.unit_measure && (
                  <div className="text-xs flex items-center gap-1 text-muted-foreground">
                    <CreditCard className="h-3 w-3" />
                    {formatUnitMeasure(plan.unit_measure)}
                  </div>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
        
        {renderPagination()}
      </>
    );
  };

  const renderListView = () => {
    const paginatedData = getPaginatedData();
    
    if (paginatedData.length === 0) {
      return renderEmptyState();
    }
    
    return (
      <>
        <div className="border rounded-md w-full overflow-x-auto">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Nome</TableHead>
                <TableHead className="whitespace-nowrap">Preço</TableHead>
                <TableHead className="whitespace-nowrap">Período de Cobrança</TableHead>
                <TableHead className="whitespace-nowrap">Período de Assinatura</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="text-right w-[80px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((plan) => (
                <TableRow 
                  key={plan.id} 
                  className={cn(
                    "cursor-pointer hover:bg-muted/50",
                    !plan.is_active && "opacity-70"
                  )}
                >
                  <TableCell className="font-extrabold" onClick={() => handleViewDetails(plan)}>
                    <div>
                      {plan.name}
                      {plan.sku && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {plan.sku}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => handleViewDetails(plan)}>
                    <div>
                      <div>{formatPrice(plan)}</div>
                      {plan.unit_measure && (
                        <div className="text-xs text-muted-foreground">
                          {formatUnitMeasure(plan.unit_measure)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => handleViewDetails(plan)}>
                    {getBillingPeriod(plan)}
                  </TableCell>
                  <TableCell onClick={() => handleViewDetails(plan)}>
                    {getSubscriptionPeriod(plan)}
                  </TableCell>
                  <TableCell onClick={() => handleViewDetails(plan)}>
                    <Badge
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-1 text-xs font-extrabold",
                        plan.is_active
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      )}
                    >
                      {plan.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="sr-only">Abrir menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Ações</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleViewDetails(plan)}>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Detalhes
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(plan)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        {plan.is_active ? (
                          <DropdownMenuItem onClick={() => handleDelete(plan.id)}>
                            <Ban className="mr-2 h-4 w-4" />
                            Inativar
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleActivate(plan.id)}>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Ativar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {renderPagination()}
      </>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="mt-4 flex justify-center">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => currentPage > 1 ? setCurrentPage(currentPage - 1) : null}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => setCurrentPage(page)}
                  isActive={currentPage === page}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => currentPage < totalPages ? setCurrentPage(currentPage + 1) : null}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="w-full space-y-2">
      {/* Hidden internal search - we're now using the searchTerm prop */}
      {!searchTerm && (
        <div className="relative w-full sm:w-80 hidden">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar plano por nome..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="pl-8 w-full"
          />
        </div>
      )}

      {/* Render either Grid or List view based on viewMode */}
      {viewMode === 'grid' ? renderGridView() : renderListView()}

      <div className="text-xs text-muted-foreground">
        Mostrando {getPaginatedData().length} de {filteredPlans.length} planos
        {!showInactive && plans.some(p => !p.is_active) && ' (planos inativos não estão sendo exibidos)'}
      </div>
    </div>
  );
} 